import * as React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useIntl } from "react-intl";
import { useMediaQuery, useTheme } from "@wayflyer/flyui";
import type {
  ChartFilterSwitchesProps,
  TemporalResolutionPickersProps,
  LegendItem,
  ReferencedChartConfig,
} from "@wayflyer/flyui-dataviz";
import {
  ChartFilterSwitches,
  TemporalResolutionPickers,
  ChartLegendContainer,
  getCandyBarMask,
  getReferencedChartConfig,
  getLegend,
  CandyBar,
} from "@wayflyer/flyui-dataviz";

import { ChartTopRow, Spinner } from "../styled";
import type { ValueType } from "../shared";

import { profitabilityTooltip } from "./helpers";

export type ProfitabilityDataPoint = {
  day: string;
  dayRaw?: string;
  thisYearsAOVValue: number | null;
  thisYearsAOVPreviousValue: number | null;
  lastYearsAOVValue: number | null;
  thisYearsCACValue: number | null;
  thisYearsCACPreviousValue: number | null;
  lastYearsCACValue: number | null;
  thisYearsMarketingValue: number | null;
  thisYearsMarketingPreviousValue: number | null;
  lastYearsMarketingValue: number | null;
  thisYearsNetQuantityValue?: number | null;
  thisYearsNetQuantityPreviousValue?: number | null;
  lastYearsNetQuantityValue?: number | null;
  thisYearsProfitValue: number | null;
  thisYearsProfitPreviousValue: number | null;
  lastYearsProfitValue: number | null;
  thisYearsROASValue: number | null;
  thisYearsROASPreviousValue: number | null;
  lastYearsROASValue: number | null;
  thisYearsSalesIncReturnsValue: number | null;
  thisYearsSalesIncReturnsPreviousValue: number | null;
  lastYearsSalesIncReturnsValue: number | null;
  thisYearsSalesValue: number | null;
  thisYearsSalesPreviousValue: number | null;
  lastYearsSalesValue: number | null;
};

const removeHiddenData = (
  thisYearEnabled: boolean,
  lastYearEnabled: boolean,
  data: ProfitabilityDataPoint[]
): ProfitabilityDataPoint[] => {
  if (!thisYearEnabled && !lastYearEnabled) {
    return [...data];
  }

  return data.map((dataPoint) => {
    return {
      ...dataPoint,
      thisYearsProfitValue: thisYearEnabled ? dataPoint.thisYearsProfitValue : null,
      thisYearsProfitPreviousValue: thisYearEnabled ? dataPoint.thisYearsProfitPreviousValue : null,
      thisYearsAOVValue: thisYearEnabled ? dataPoint.thisYearsAOVValue : null,
      thisYearsAOVPreviousValue: thisYearEnabled ? dataPoint.thisYearsAOVPreviousValue : null,
      thisYearsMarketingValue: thisYearEnabled ? dataPoint.thisYearsMarketingValue : null,
      thisYearsMarketingPreviousValue: thisYearEnabled ? dataPoint.thisYearsMarketingPreviousValue : null,
      thisYearsROASValue: thisYearEnabled ? dataPoint.thisYearsROASValue : null,
      thisYearsROASPreviousValue: thisYearEnabled ? dataPoint.thisYearsROASPreviousValue : null,
      thisYearsCACValue: thisYearEnabled ? dataPoint.thisYearsCACValue : null,
      thisYearsCACPreviousValue: thisYearEnabled ? dataPoint.thisYearsCACPreviousValue : null,
      thisYearsSalesValue: thisYearEnabled ? dataPoint.thisYearsSalesValue : null,
      thisYearsSalesPreviousValue: thisYearEnabled ? dataPoint.thisYearsSalesPreviousValue : null,
      thisYearsSalesIncReturnsValue: thisYearEnabled ? dataPoint.thisYearsSalesIncReturnsValue : null,
      thisYearsSalesIncReturnsPreviousValue: thisYearEnabled ? dataPoint.thisYearsSalesIncReturnsPreviousValue : null,
      lastYearsProfitValue: lastYearEnabled ? dataPoint.lastYearsProfitValue : null,
      lastYearsAOVValue: lastYearEnabled ? dataPoint.lastYearsAOVValue : null,
      lastYearsMarketingValue: lastYearEnabled ? dataPoint.lastYearsMarketingValue : null,
      lastYearsROASValue: lastYearEnabled ? dataPoint.lastYearsROASValue : null,
      lastYearsCACValue: lastYearEnabled ? dataPoint.lastYearsCACValue : null,
      lastYearsSalesValue: lastYearEnabled ? dataPoint.lastYearsSalesValue : null,
      lastYearsSalesIncReturnsValue: lastYearEnabled ? dataPoint.lastYearsSalesIncReturnsValue : null,
    };
  });
};

// Hide the Profit legend if no data yet sent
const hasProfitData = (data: ProfitabilityDataPoint[]) => {
  return data.length > 0
    ? data[0].thisYearsProfitValue !== undefined || data[0].lastYearsProfitValue !== undefined
    : false;
};

export interface ProfitabilityChartProps {
  data: ProfitabilityDataPoint[];
  resolutionAbreviation: "DoD" | "WoW" | "MoM" | "YoY";
  thisYearEnabled?: boolean;
  lastYearEnabled?: boolean;
  aovEnabled?: boolean;
  cacEnabled?: boolean;
  marketingEnabled?: boolean;
  profitEnabled?: boolean;
  roasEnabled?: boolean;
  salesEnabled?: boolean;
  salesIncReturnsEnabled?: boolean;
  formatValue: (rawValue: ValueType, isROAS?: boolean) => string | number;
  loading?: boolean;
  //used for testing, to ensure responsiveContainer has a forced width and can then render the chart for snapshots
  width?: number | string;
  temporalResPickerProps?: TemporalResolutionPickersProps;
  chartFilterSwitchesProps?: ChartFilterSwitchesProps;
}

/**
 * ProfitabilityChart component
 */

export const ProfitabilityChart = ({
  data,
  thisYearEnabled = true,
  lastYearEnabled = false,
  profitEnabled = true,
  aovEnabled = true,
  cacEnabled = true,
  marketingEnabled = true,
  roasEnabled = true,
  salesEnabled = true,
  salesIncReturnsEnabled = true,
  resolutionAbreviation = "MoM",
  formatValue,
  width, // undefined leaves height determine width, >0 is just used for snapshots to ensure chart is rendered
  loading = false,
  temporalResPickerProps,
  chartFilterSwitchesProps,
}: ProfitabilityChartProps): UIElement => {
  const query = useMediaQuery();
  const intl = useIntl();
  const mediumWidth = query("sm").windowGt;
  const theme = useTheme();

  const chartConfig: ReferencedChartConfig = getReferencedChartConfig(mediumWidth);

  const tickFormatter = (value: any): string => {
    return formatValue(value) as string;
  };

  const tickFormatterROAS = (value: any): string => {
    return formatValue(value, true) as string;
  };

  const dataPoints = removeHiddenData(thisYearEnabled, lastYearEnabled, data);
  const showingBothYears = thisYearEnabled && lastYearEnabled;

  if (loading) {
    return <Spinner />;
  }

  const chartColorMap = {
    AOV: theme.palette.dataviz.categorical.category4,
    CAC: theme.palette.dataviz.categorical.category5,
    Marketing: theme.palette.dataviz.categorical.category3,
    ROAS: theme.palette.dataviz.categorical.category7,
    Sales: theme.palette.dataviz.categorical.category1,
    SalesIncReturns: theme.palette.dataviz.categorical.category2,
    Profit: theme.palette.dataviz.categorical.category6,
  };

  const ProfitLabel = intl.formatMessage({ id: "Overview chart: Net Profit", defaultMessage: "Net Profit" });
  const ProfitLabelThisYear = intl.formatMessage({
    id: "Overview chart: Net Profit this year",
    defaultMessage: "Net Profit this year",
  });
  const ProfitLabelLastYear = intl.formatMessage({
    id: "Overview chart: Net Profit last year",
    defaultMessage: "Net Profit last year",
  });
  const AOVLabel = intl.formatMessage({
    id: "Overview chart: Average Order Value",
    defaultMessage: "Average Order Value",
  });
  const AOVLabelThisYear = intl.formatMessage({
    id: "Overview chart: Average Order Value this year",
    defaultMessage: "Average Order Value this year",
  });
  const AOVLabelLastYear = intl.formatMessage({
    id: "Overview chart: Average Order Value last year",
    defaultMessage: "Average Order Value last year",
  });
  const CACLabel = intl.formatMessage({
    id: "Overview chart: Customer Acquisition Cost",
    defaultMessage: "Customer Acquisition Cost",
  });
  const CACLabelThisYear = intl.formatMessage({
    id: "Overview chart: Customer Acquisition Cost this year",
    defaultMessage: "Customer Acquisition Cost this year",
  });
  const CACLabelLastYear = intl.formatMessage({
    id: "Overview chart: Customer Acquisition Cost last year",
    defaultMessage: "Customer Acquisition Cost last year",
  });
  const MarketingLabel = intl.formatMessage({ id: "Overview chart: Marketing", defaultMessage: "Marketing" });
  const MarketingLabelThisYear = intl.formatMessage({
    id: "Overview chart: Marketing this year",
    defaultMessage: "Marketing this year",
  });
  const MarketingLabelLastYear = intl.formatMessage({
    id: "Overview chart: Marketing last year",
    defaultMessage: "Marketing last year",
  });
  const ROASLabel = intl.formatMessage({
    id: "Overview chart: Marketing Efficiency Ratio",
    defaultMessage: "Marketing Efficiency Ratio",
  });
  const ROASLabelThisYear = intl.formatMessage({
    id: "Overview chart: Marketing Efficiency Ratio this year",
    defaultMessage: "Marketing Efficiency Ratio this year",
  });
  const ROASLabelLastYear = intl.formatMessage({
    id: "Overview chart: Marketing Efficiency Ratio last year",
    defaultMessage: "Marketing Efficiency Ratio last year",
  });
  const SalesLabel = intl.formatMessage({ id: "Overview chart: Total Sales", defaultMessage: "Total Sales" });
  const SalesLabelThisYear = intl.formatMessage({
    id: "Overview chart: Total Sales this year",
    defaultMessage: "Total Sales this year",
  });
  const SalesLabelLastYear = intl.formatMessage({
    id: "Overview chart: Total Sales last year",
    defaultMessage: "Total Sales last year",
  });
  const SalesIncReturnsLabel = intl.formatMessage({
    id: "Overview chart: Gross Revenue",
    defaultMessage: "Gross Revenue",
  });
  const SalesIncReturnsLabelThisYear = intl.formatMessage({
    id: "Overview chart: Gross Revenue this year",
    defaultMessage: "Gross Revenue this year",
  });
  const SalesIncReturnsLabelLastYear = intl.formatMessage({
    id: "Overview chart: Gross Revenue last year",
    defaultMessage: "Gross Revenue last year",
  });

  const legendItems: LegendItem[] = [];
  if (profitEnabled && hasProfitData(data)) {
    legendItems.push({
      label: ProfitLabel,
      color: chartColorMap.Profit,
      isBar: true,
    });
  }
  if (salesEnabled) {
    legendItems.push({
      label: SalesLabel,
      color: chartColorMap.Sales,
    });
  }
  if (salesIncReturnsEnabled) {
    legendItems.push({
      label: SalesIncReturnsLabel,
      color: chartColorMap.SalesIncReturns,
    });
  }
  if (marketingEnabled) {
    legendItems.push({
      label: MarketingLabel,
      color: chartColorMap.Marketing,
    });
  }
  if (roasEnabled) {
    legendItems.push({
      label: ROASLabel,
      color: chartColorMap.ROAS,
    });
  }
  if (aovEnabled) {
    legendItems.push({
      label: AOVLabel,
      color: chartColorMap.AOV,
    });
  }
  if (cacEnabled) {
    legendItems.push({
      label: CACLabel,
      color: chartColorMap.CAC,
    });
  }

  const thisYearBarOpacity = chartConfig.barFillOpacity + (lastYearEnabled ? 0.2 : 0);

  return dataPoints ? (
    <ChartLegendContainer>
      <ChartTopRow>
        {temporalResPickerProps ? (
          <TemporalResolutionPickers {...temporalResPickerProps} className="no-padding" />
        ) : null}
        {getLegend(legendItems, thisYearBarOpacity)}
        {chartFilterSwitchesProps ? <ChartFilterSwitches {...chartFilterSwitchesProps} /> : null}
      </ChartTopRow>
      <ResponsiveContainer height={chartConfig.height} width={(width ?? "99%") as number}>
        <ComposedChart data={dataPoints} margin={chartConfig.margin}>
          {mediumWidth ? (
            <CartesianGrid
              strokeWidth={chartConfig.gridStrokeWidth}
              stroke={theme.palette.bg.neutral.strong}
              {...({ yAxisId: "left" } as any)}
            />
          ) : null}
          {mediumWidth ? <XAxis dataKey="day" height={chartConfig.xAxisHeight} axisLine={!profitEnabled} /> : null}
          {mediumWidth ? (
            <YAxis
              tick={{ fontSize: chartConfig.yAxisFontSize }}
              tickFormatter={tickFormatter}
              axisLine={!profitEnabled}
              yAxisId="left"
            />
          ) : null}
          {mediumWidth && roasEnabled ? (
            <YAxis
              tick={{ fontSize: chartConfig.yAxisFontSize }}
              tickFormatter={tickFormatterROAS}
              axisLine={!profitEnabled}
              yAxisId="right"
              orientation="right"
            >
              <Label
                angle={chartConfig.yAxisLabelAngle}
                position="right"
                style={{ textAnchor: "middle" }}
                fill={theme.palette.content.neutral.strong}
              >
                {ROASLabel}
              </Label>
            </YAxis>
          ) : null}
          {mediumWidth ? <Tooltip content={profitabilityTooltip(formatValue, resolutionAbreviation)} /> : null}
          {profitEnabled ? (
            <>
              <ReferenceLine y={0} stroke={chartConfig.referenceLineColor} yAxisId="left" />
              {thisYearEnabled ? (
                <Bar
                  name={showingBothYears ? ProfitLabelThisYear : ProfitLabel}
                  type="monotone"
                  dataKey="thisYearsProfitValue"
                  fill={chartColorMap["Profit"]}
                  fillOpacity={thisYearBarOpacity}
                  isAnimationActive={false}
                  yAxisId="left"
                />
              ) : null}
              {lastYearEnabled ? (
                <>
                  {getCandyBarMask()}
                  <Bar
                    name={showingBothYears ? ProfitLabelLastYear : ProfitLabel}
                    type="monotone"
                    dataKey="lastYearsProfitValue"
                    shape={<CandyBar fill={chartColorMap["Profit"]} fillOpacity={thisYearBarOpacity} />}
                    fill={chartColorMap["Profit"]}
                    isAnimationActive={false}
                    yAxisId="left"
                  />
                </>
              ) : null}
            </>
          ) : null}
          {aovEnabled
            ? getLineChart(
                "AOV",
                thisYearEnabled,
                lastYearEnabled,
                mediumWidth,
                chartColorMap["AOV"],
                showingBothYears ? AOVLabelThisYear : AOVLabel,
                showingBothYears ? AOVLabelLastYear : AOVLabel
              )
            : null}
          {cacEnabled
            ? getLineChart(
                "CAC",
                thisYearEnabled,
                lastYearEnabled,
                mediumWidth,
                chartColorMap["CAC"],
                showingBothYears ? CACLabelThisYear : CACLabel,
                showingBothYears ? CACLabelLastYear : CACLabel
              )
            : null}
          {marketingEnabled
            ? getLineChart(
                "Marketing",
                thisYearEnabled,
                lastYearEnabled,
                mediumWidth,
                chartColorMap["Marketing"],
                showingBothYears ? MarketingLabelThisYear : MarketingLabel,
                showingBothYears ? MarketingLabelLastYear : MarketingLabel
              )
            : null}
          {roasEnabled
            ? getLineChart(
                "ROAS",
                thisYearEnabled,
                lastYearEnabled,
                mediumWidth,
                chartColorMap["ROAS"],
                showingBothYears ? ROASLabelThisYear : ROASLabel,
                showingBothYears ? ROASLabelLastYear : ROASLabel
              )
            : null}
          {salesEnabled
            ? getLineChart(
                "Sales",
                thisYearEnabled,
                lastYearEnabled,
                mediumWidth,
                chartColorMap["Sales"],
                showingBothYears ? SalesLabelThisYear : SalesLabel,
                showingBothYears ? SalesLabelLastYear : SalesLabel
              )
            : null}
          {salesIncReturnsEnabled
            ? getLineChart(
                "SalesIncReturns",
                thisYearEnabled,
                lastYearEnabled,
                mediumWidth,
                chartColorMap["SalesIncReturns"],
                showingBothYears ? SalesIncReturnsLabelThisYear : SalesIncReturnsLabel,
                showingBothYears ? SalesIncReturnsLabelLastYear : SalesIncReturnsLabel
              )
            : null}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartLegendContainer>
  ) : null;
};

const getLineChart = (
  dataKeyKey: string,
  thisYearEnabled: boolean,
  lastYearEnabled: boolean,
  isDesktop: boolean,
  thisYearColor: string,
  thisYearTitle: string,
  lastYearTitle: string
) => {
  return (
    <>
      {thisYearEnabled ? (
        <Line
          name={thisYearTitle}
          type="monotone"
          dataKey={`thisYears${dataKeyKey}Value`}
          strokeWidth={thisYearEnabled ? "1.5" : "0"}
          stroke={thisYearColor}
          dot={isDesktop && thisYearEnabled ? true : false}
          activeDot={thisYearEnabled ? true : false}
          isAnimationActive={false}
          yAxisId={dataKeyKey === "ROAS" ? "right" : "left"}
        />
      ) : null}
      {lastYearEnabled ? (
        <Line
          name={lastYearTitle}
          type="monotone"
          dataKey={`lastYears${dataKeyKey}Value`}
          strokeWidth={lastYearEnabled ? "1" : "0"}
          stroke={thisYearColor}
          dot={isDesktop && lastYearEnabled ? true : false}
          activeDot={lastYearEnabled ? true : false}
          isAnimationActive={false}
          strokeDasharray="3 3"
          yAxisId={dataKeyKey === "ROAS" ? "right" : "left"}
          legendType="none"
        />
      ) : null}
    </>
  );
};
